import React, { useState } from 'react';
import { Form, Button, Row, Col, Alert, InputGroup, Modal, Toast } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDate } from '../../utils/dateUtils';
import { handleError } from '../../utils/errorHandling';
import { sanitizeInput, sanitizeName, sanitizeBsb, sanitizeAccountNumber, sanitizeNumeric } from '../../utils/sanitize';
import { addBankAccount } from '../../services/BankAccountService';

const validateBsb = (bsb) => {
  if (!bsb) return true;
  const bsbRegex = /^(\d{3}[-\s]?\d{3})$/;
  return bsbRegex.test(bsb) ? true : 'BSB must be 6 digits in format: XXX-XXX, XXXXXX, or XXX XXX';
};

const validateAccNum = (accNum) => {
  if (!accNum) return true;
  const accNumRegex = /^\d{4,20}$/;
  return accNumRegex.test(accNum.trim()) ? true : 'Account number must be between 4-20 digits';
};

const validateAmounts = (amount) => {
  const numAmount = parseFloat(amount);
  return !isNaN(numAmount) &&
         numAmount >= 0 &&
         numAmount < 1000000 &&
         amount.toString().length <= 10;
};

const validateDates = (startDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!startDate) return 'Start date is required';
  if (startDate < today) return 'Start date cannot be in the past';
  return true;
};


const validateRequiredFields = (data) => {
  const errors = {};
  if (!data.bankAccNickname?.trim()) errors.nickname = 'Nickname is required';
  if (!data.bank?.trim()) errors.bank = 'Bank name is required';
  if (!data.activeStartDate) errors.activeStartDate = 'Start date is required';
  if (!data.fulfil) errors.fulfil = 'Fulfillment frequency is required';
  return errors;
};

const validateLengths = (data) => {
  const errors = {};
  const maxLengths = {
    nickname: 50,
    bank: 50,
    accNum: 20
  };

  Object.entries(maxLengths).forEach(([field, maxLength]) => {
    if (data[field]?.length > maxLength) {
      errors[field] = `${field} exceeds maximum length of ${maxLength}`;
    }
  });
  return errors;
};

const AddBankAccForm = ({ onSuccess }) => {
  const [bankAccNickname, setBankAccNickname] = useState('');
  const [bank, setBank] = useState('');
  const [nameOnAcc, setNameOnAcc] = useState('');
  const [bsb, setBsb] = useState('');
  const [accNum, setAccNum] = useState('');
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const [requiredAmount, setRequiredAmount] = useState('0.00');
  const [fulfil, setFulfil] = useState('weekly');
  const [balance, setBalance] = useState('0.00');
  const [lastSubmitTime, setLastSubmitTime] = useState(Date.now());
  const [errorMessage, setErrorMessage] = useState('');
  // States for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingBankAccount, setPendingBankAccount] = useState(null);
  // State for toast
  const [showToast, setShowToast] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    switch(name) {
      case 'bankAccNickname':
        setBankAccNickname(sanitizeInput(value));
        break;
      case 'bank':
        // Allow all characters up to 50 chars
        setBank(sanitizeInput(value.slice(0, 50)));
        break;
      case 'nameOnAcc':
        // Allow letters and spaces
        setNameOnAcc(sanitizeName(value));
        break;
      case 'bsb':
        setBsb(sanitizeBsb(value));
        break;
      case 'accNum':
        setAccNum(sanitizeAccountNumber(value));
        break;
      case 'requiredAmount':
        setRequiredAmount(sanitizeNumeric(value));
        break;
      case 'fulfil':
        setFulfil(value);
        break;
      default:
        if (name === 'activeStartDate') {
          setActiveStartDate(value);
        }
    }
  };

  const validateForm = (data) => {
    const errors = {};
    errors.bsb = validateBsb(data.bsb);
    errors.accNum = validateAccNum(data.accNum);
    errors.balance = validateAmounts(data.balance);
    errors.requiredAmount = validateAmounts(data.requiredAmount);
    errors.dates = validateDates(data.activeStartDate, null);
    Object.assign(errors, validateRequiredFields(data));
    Object.assign(errors, validateLengths(data));
    return errors;
  };

  const resetForm = () => {
    setBankAccNickname('');
    setBank('');
    setNameOnAcc('');
    setBsb('');
    setAccNum('');
    setActiveStartDate(new Date());
    setRequiredAmount('0.00');
    setFulfil('weekly');
    setBalance('0.00');
    setErrorMessage(''); // Clear any error messages when resetting
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (Date.now() - lastSubmitTime < 1000) {
      setErrorMessage('Please wait before submitting again');
      return;
    }
    setLastSubmitTime(Date.now());

    const formData = {
      bankAccNickname,
      bank,
      nameOnAcc,
      bsb: bsb.replace(/[-\s]/g, ''),
      accNum: accNum.replace(/\s/g, ''),
      activeStartDate,
      requiredAmount,
      fulfil,
      balance: '0.00'
    };

    const validationErrors = validateForm(formData);
    if (Object.values(validationErrors).some(error => error !== true)) {
      const errorMessages = Object.entries(validationErrors)
        .filter(([_, value]) => typeof value === 'string')
        .map(([_, message], index) => (
          <React.Fragment key={index}>
            {message}
            <br />
          </React.Fragment>
        ));

      setErrorMessage(errorMessages);
      return;
    }

    // Clear any previous error messages
    setErrorMessage('');

    const newBankAccount = {
      nickname: sanitizeInput(bankAccNickname),
      bank: sanitizeInput(bank),
      acc_name: sanitizeName(nameOnAcc),
      bsb: sanitizeBsb(bsb).replace(/[-\s]/g, ''),
      acc_num: sanitizeAccountNumber(accNum).replace(/\s/g, ''),
      active_start: formatDate(activeStartDate),
      balance: '0.00',
      required_amount: parseFloat(sanitizeNumeric(requiredAmount)),
      fulfil: fulfil,
      use_as_balance: false,
      include_in_tracking: false
    };

    setPendingBankAccount(newBankAccount);
    setShowConfirmModal(true);
  };

  // Confirmation handler showing toast on success
  const handleConfirm = async () => {
    try {
      console.log('AddBankAccForm: Attempting to create bank account');
      const addedAccount = await addBankAccount(pendingBankAccount);
      console.log('AddBankAccForm: Bank account created successfully');
      
      // Success: Close modal, show toast, reset form
      setShowConfirmModal(false);
      setShowToast(true);
      resetForm();
      setErrorMessage(''); // Clear any previous errors
      
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } catch (error) {
      console.error('AddBankAccForm: Error creating bank account:', error);
      
      // Check if this is actually an error or just a response parsing issue
      if (error?.response?.status === 201) {
        console.log('AddBankAccForm: Received 201 status, treating as success');
        // This is actually a success, just poorly handled
        setShowConfirmModal(false);
        setShowToast(true);
        resetForm();
        setErrorMessage('');
        
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
        return;
      }
      
      // Real error - keep modal open and show error
      const message = handleError(error, 'AddBankAccForm');
      setErrorMessage(message);
      console.error('AddBankAccForm: Setting error message:', message);
    }
  };

  return (
    <>
      <Form onSubmit={handleFormSubmit}>
        {errorMessage && (
          <Alert variant="danger">{errorMessage}</Alert>
        )}

        <Form.Group className="mb-3" controlId="bankAccNickname">
          <Form.Label>Account Nickname:</Form.Label>
          <Form.Control
            type="text"
            value={bankAccNickname}
            onChange={(e) => handleInputChange({ target: { name: 'bankAccNickname', value: e.target.value } })}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="requiredAmount">
          <Form.Label>Amount Required:</Form.Label>
          <Row>
            <Col xs={8}>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  value={requiredAmount}
                  onChange={(e) => handleInputChange({ target: { name: 'requiredAmount', value: e.target.value } })}
                  placeholder="Enter amount"
                />
              </InputGroup>
            </Col>
            <Col xs={4}>
              <Form.Select
                value={fulfil}
                onChange={(e) => handleInputChange({ target: { name: 'fulfil', value: e.target.value } })}
              >
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
              </Form.Select>
            </Col>
          </Row>
        </Form.Group>

        <Form.Group className="mb-3" controlId="bank">
          <Form.Label>Bank:</Form.Label>
          <Form.Control
            type="text"
            value={bank}
            onChange={(e) => handleInputChange({ target: { name: 'bank', value: e.target.value } })}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="nameOnAcc">
          <Form.Label>Account Name:</Form.Label>
          <Form.Control
            type="text"
            value={nameOnAcc}
            onChange={(e) => handleInputChange({ target: { name: 'nameOnAcc', value: e.target.value } })}
          />
        </Form.Group>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="bsb">
              <Form.Label>BSB:</Form.Label>
              <Form.Control
                type="text"
                value={bsb}
                onChange={(e) => handleInputChange({ target: { name: 'bsb', value: e.target.value } })}
                placeholder="000-000"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3" controlId="accNum">
              <Form.Label>Account Number:</Form.Label>
              <Form.Control
                type="text"
                value={accNum}
                onChange={(e) => handleInputChange({ target: { name: 'accNum', value: e.target.value } })}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3" controlId="activeStartDate">
          <Form.Label>Active from:</Form.Label>
          <br />
          <DatePicker
            selected={activeStartDate}
            onChange={(date) => setActiveStartDate(date)}
            dateFormat="yyyy-MM-dd"
            className="form-control"
            minDate={new Date()}
          />
        </Form.Group>

        <Button variant="primary" type="submit">
          Add Bank Account
        </Button>
      </Form>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bank Account Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {pendingBankAccount && (
            <>
              <p><strong>Account Nickname:</strong> {pendingBankAccount.nickname}</p>
              <p><strong>Bank:</strong> {pendingBankAccount.bank}</p>
              <p><strong>Account Name:</strong> {pendingBankAccount.acc_name}</p>
              <p><strong>BSB:</strong> {pendingBankAccount.bsb}</p>
              <p><strong>Account Number:</strong> {pendingBankAccount.acc_num}</p>
              <p><strong>Required Amount:</strong> ${pendingBankAccount.required_amount}</p>
              <p><strong>Frequency:</strong> {pendingBankAccount.fulfil}</p>
              <p><strong>Active From:</strong> {pendingBankAccount.active_start}</p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Change
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Toast */}
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        delay={4000}
        autohide
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          transform: 'none',
          zIndex: 9999,
          minWidth: '300px',
          backgroundColor: '#d4edda',
          borderColor: '#c3e6cb',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        className="text-success"
      >
        <Toast.Header className="bg-success text-white">
          <strong className="me-auto">✅ Success!</strong>
        </Toast.Header>
        <Toast.Body style={{ color: '#155724', fontWeight: 'bold' }}>
          Bank account successfully added and ready to use!
        </Toast.Body>
      </Toast>
    </>
  );
};

export default AddBankAccForm;
